// src/index.ts
import { z } from "zod";
var platformSchema = z.enum(["wb"]);
var analysisModeSchema = z.enum(["standalone", "wb"]);
var userRoleSchema = z.enum(["admin", "user"]);
var userDecisionSchema = z.enum(["unmarked", "keep", "discard"]);
function platformsForAnalysisMode(mode) {
  return mode === "standalone" ? [] : [mode];
}
var loginRequestSchema = z.object({
  username: z.string().trim().min(2).max(64),
  password: z.string().min(6).max(256),
  rememberMe: z.boolean().default(true)
});
var registerRequestSchema = z.object({
  username: z.string().trim().min(2).max(64),
  password: z.string().min(6).max(256)
});
var publicUserSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  role: userRoleSchema,
  isActive: z.boolean(),
  version: z.number().int().positive()
});
var createUserRequestSchema = z.object({
  username: z.string().trim().min(2).max(64),
  password: z.string().min(6).max(256),
  role: userRoleSchema
});
var updateUserRequestSchema = z.object({
  role: userRoleSchema.optional(),
  isActive: z.boolean().optional(),
  expectedVersion: z.number().int().positive()
}).refine((value) => value.role !== void 0 || value.isActive !== void 0, {
  message: "At least one field must be updated."
});
var resetPasswordRequestSchema = z.object({
  password: z.string().min(6).max(256),
  expectedVersion: z.number().int().positive()
});
var keywordSearchRequestSchema = z.object({
  query: z.object({
    text: z.string().max(300).default(""),
    mode: z.enum(["exact", "all_words", "any_word"]).default("all_words"),
    exclude: z.array(z.string().max(100)).max(20).default([]),
    scope: z.enum(["current_platform", "all_platforms"]).default("current_platform")
  }).default({ text: "", mode: "all_words", exclude: [], scope: "current_platform" }),
  filters: z.object({
    decisions: z.array(userDecisionSchema).default(["unmarked", "keep"]),
    includeHighRisk: z.boolean().default(false),
    includeDiscarded: z.boolean().default(false),
    score: z.object({
      gte: z.number().min(0).max(100).optional(),
      lte: z.number().min(0).max(100).optional()
    }).optional(),
    supplyDemandRatio: z.object({ gte: z.number().min(0).optional(), lte: z.number().min(0).optional() }).optional()
  }).default({ decisions: ["unmarked", "keep"], includeHighRisk: false, includeDiscarded: false }),
  sort: z.array(
    z.object({
      field: z.enum([
        "updatedAt",
        "originalKeyword",
        "currentDemand",
        "supplyDemandRatio",
        "compositeScore"
      ]),
      direction: z.enum(["asc", "desc"])
    })
  ).max(3).default([{ field: "updatedAt", direction: "desc" }]),
  cursor: z.string().max(512).nullable().default(null),
  limit: z.number().int().min(1).max(200).default(50)
});
var updateDecisionRequestSchema = z.object({
  decision: userDecisionSchema,
  expectedVersion: z.number().int().positive()
});
var batchUpdateDecisionRequestSchema = z.object({
  decision: userDecisionSchema,
  records: z.array(z.object({ id: z.string().uuid(), expectedVersion: z.number().int().positive() })).min(1).max(1e3)
});
var createUploadRequestSchema = z.object({
  platform: platformSchema,
  fileName: z.string().min(1).max(255),
  fileSize: z.number().int().positive(),
  mimeType: z.string().min(1).max(160),
  checksum: z.string().regex(/^[a-fA-F0-9]{64}$/),
  chunkSize: z.number().int().min(64 * 1024).max(32 * 1024 * 1024)
});
var preferenceValueSchema = z.record(z.string(), z.unknown());
var createDictionaryEntrySchema = z.object({
  canonicalKeyword: z.string().trim().min(1).max(200),
  chineseTranslation: z.string().trim().min(1).max(200).nullable().default(null),
  standardProductName: z.string().trim().min(1).max(200).nullable().default(null),
  productType: z.string().trim().min(1).max(120).nullable().default(null),
  mainKeyword: z.string().trim().min(1).max(200).nullable().default(null),
  aliases: z.array(
    z.object({
      value: z.string().trim().min(1).max(200),
      kind: z.enum(["synonym", "grammar_variant", "related"])
    })
  ).max(100).default([]),
  locked: z.boolean().default(true)
});
var createPromptVersionSchema = z.object({
  systemPrompt: z.string().trim().min(20).max(2e4),
  userTemplate: z.string().trim().min(5).max(2e4),
  responseSchema: z.record(z.string(), z.unknown())
});
var keywordProcessingActionSchema = z.object({
  action: z.enum(["ai_review", "recalculate_score", "rerun_risk", "refresh_dictionary"]),
  records: z.array(z.string().uuid()).min(1).max(1e3)
});
var competitorRefreshSchema = z.object({
  links: z.array(z.string().url()).max(10).default([]),
  force: z.boolean().default(false)
});
var competitorSampleUpdateSchema = z.object({
  selected: z.boolean().optional(),
  locked: z.boolean().optional(),
  expectedVersion: z.number().int().positive()
});
var productMaterialSchema = z.object({
  rawText: z.string().trim().min(1).max(1e5),
  sourceLink: z.string().url().nullable().default(null)
});
var updateExtractedAttributeSchema = z.object({
  value: z.string().trim().min(1).max(500),
  expectedVersion: z.number().int().positive()
});
var keywordRelationsSchema = z.object({
  relations: z.array(
    z.object({
      keywordText: z.string().trim().min(1).max(200),
      chineseTranslation: z.string().trim().min(1).max(200).nullable(),
      usageKind: z.enum(["title", "description", "attribute", "unrelated", "risk", "duplicate"]),
      selected: z.boolean(),
      relevance: z.number().min(0).max(1).nullable()
    })
  ).max(100)
});
var productCardDraftSchema = z.object({
  productName: z.string().trim().min(1).max(200),
  materialId: z.string().uuid().nullable().default(null),
  titleKeywords: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
  descriptionKeywords: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
  category: z.string().trim().min(1).max(200).nullable().default(null),
  candidates: z.number().int().min(1).max(4).default(1)
});
var saveProductCardSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().min(1).max(2e4),
  attributes: z.record(z.string(), z.string()),
  keywords: z.array(z.string().trim().min(1).max(200)).max(100),
  category: z.string().trim().min(1).max(200).nullable(),
  sourceStatus: z.enum(["live", "category_cache", "platform_default"])
});
var currencySchema = z.enum(["CNY", "USD", "RUB"]);
var interpretProductIdeaSchema = z.object({
  idea: z.string().trim().min(1).max(2e3),
  analysisMode: analysisModeSchema.default("standalone"),
  debug: z.boolean().optional().default(false)
});
var suggestSourceTypeSchema = z.enum([
  "local_dictionary",
  "yandex_suggest",
  "google_suggest",
  "wb_suggest",
  "ai_supplement"
]);
var createResearchRunSchema = z.object({
  idea: z.string().trim().min(1).max(2e3),
  entityZh: z.string().trim().min(1).max(200),
  productTypeZh: z.string().trim().min(1).max(120).nullable().default(null),
  useCases: z.array(z.string().trim().min(1).max(120)).max(5).default([]),
  coreQueries: z.array(
    z.object({
      keywordRu: z.string().trim().min(1).max(200),
      translationZh: z.string().trim().min(1).max(200).nullable(),
      confidence: z.number().min(0).max(1)
    })
  ).min(1).max(3),
  analysisMode: analysisModeSchema.default("standalone")
});
var refreshResearchSuggestionsSchema = z.object({
  force: z.boolean().default(false)
});
var selectResearchKeywordsSchema = z.object({
  keywordIds: z.array(z.string().uuid()).max(100)
});
var wordstatOpenedSchema = z.object({
  keywordId: z.string().uuid()
});
var manualWordstatSchema = z.object({
  keywordId: z.string().uuid(),
  frequency: z.number().finite().nonnegative().nullable().default(null),
  period: z.string().trim().min(1).max(120).nullable().default(null),
  trend: z.enum(["rising", "stable", "declining", "seasonal", "insufficient"]).nullable().default(null),
  notes: z.string().trim().max(2e3).nullable().default(null)
});
var researchCompetitorRefreshSchema = z.object({
  platforms: z.array(platformSchema).min(1).max(1).transform((items) => [...new Set(items)])
});
var researchDecisionSchema = z.object({
  competitorEvidence: z.array(
    z.object({
      platform: platformSchema,
      keywordId: z.string().uuid(),
      itemCount: z.number().int().nonnegative(),
      relevantCount: z.number().int().nonnegative(),
      medianPrice: z.number().finite().nonnegative().nullable(),
      medianReviews: z.number().finite().nonnegative().nullable(),
      brandCount: z.number().int().nonnegative().nullable()
    })
  ).max(20).default([])
});
var seoPromptKeywordContextSchema = z.object({
  keyword: z.string().trim().min(1).max(200),
  heat: z.number().finite().nonnegative().nullable().optional(),
  competition: z.number().finite().nonnegative().nullable().optional(),
  supplyDemandRatio: z.number().finite().nonnegative().nullable().optional(),
  relevance: z.number().min(0).max(1).nullable().optional(),
  sourceCount: z.number().int().nonnegative().nullable().optional()
});
var seoPlatformSchema = z.enum(["wb", "ozon"]);
var seoPromptTemplateVersionSchema = z.enum(["wb-seo-v1", "ozon-seo-v1"]);
var extractSeoProductRequestSchema = z.object({
  rawProductText: z.string().trim().min(1, "\u8BF7\u7C98\u8D34\u5546\u54C1\u8D44\u6599").max(1e5),
  platform: seoPlatformSchema.nullable().default(null)
});
var seoPromptTextArraySchema = z.array(z.string().trim().min(1).max(500)).max(50).default([]);
var seoPromptProductSchema = z.object({
  productNameCn: z.string().trim().max(500),
  productTypeCn: z.string().trim().max(500),
  coreFunctions: seoPromptTextArraySchema,
  features: seoPromptTextArraySchema,
  materials: seoPromptTextArraySchema,
  dimensions: seoPromptTextArraySchema,
  colors: seoPromptTextArraySchema,
  quantity: z.string().trim().max(500),
  includedItems: seoPromptTextArraySchema,
  useScenarios: seoPromptTextArraySchema,
  targetUsers: seoPromptTextArraySchema,
  sellingPoints: seoPromptTextArraySchema
});
var seoPromptInformationRisksSchema = z.object({
  uncertain: seoPromptTextArraySchema,
  conflicting: seoPromptTextArraySchema,
  prohibitedClaims: seoPromptTextArraySchema
});
var seoProductEvidenceSchema = z.object({
  field: z.string().trim().min(1).max(200),
  value: z.string().trim().min(1).max(500),
  sourceText: z.string().trim().min(1).max(1e3)
});
var seoProductExtractionResultSchema = z.object({
  product: seoPromptProductSchema,
  informationRisks: seoPromptInformationRisksSchema,
  evidence: z.array(seoProductEvidenceSchema).max(100).default([]),
  multipleProductsDetected: z.boolean().default(false)
});
var generateSeoPromptRequestSchema = z.object({
  confirmedProduct: seoPromptProductSchema,
  informationRisks: seoPromptInformationRisksSchema,
  platform: seoPlatformSchema.default("wb"),
  analysisContext: z.object({
    keywords: z.array(seoPromptKeywordContextSchema).max(200).default([])
  }).default({ keywords: [] })
});
var seoPromptGenerationResultSchema = z.object({
  platform: seoPlatformSchema,
  promptTemplateVersion: seoPromptTemplateVersionSchema,
  product: seoPromptProductSchema,
  informationRisks: seoPromptInformationRisksSchema,
  keywords: z.object({
    primaryKeyword: z.string().trim().max(200),
    primaryKeywordTranslationZh: z.string().trim().max(300).optional(),
    coreKeywords: z.array(
      z.object({
        keyword: z.string().trim().min(1).max(200),
        translationZh: z.string().trim().max(300).optional(),
        heat: z.number().finite().nonnegative().nullable().optional(),
        heatSupported: z.boolean().optional(),
        reason: z.string().trim().min(1).max(1e3)
      })
    ).max(5).default([]),
    longTailKeywords: z.array(
      z.object({
        keyword: z.string().trim().min(1).max(300),
        translationZh: z.string().trim().max(300).optional(),
        sourceType: z.enum(["analysis_keyword", "web_verified_synonym", "attribute_combination"]),
        basedOn: z.array(z.string().trim().min(1).max(500)).min(1).max(50),
        reason: z.string().trim().min(1).max(1e3),
        heat: z.number().finite().nonnegative().nullable(),
        heatSupported: z.boolean(),
        confidence: z.number().min(0).max(1)
      })
    ).max(10).default([]),
    optionalKeywords: z.array(
      z.object({
        keyword: z.string().trim().min(1).max(300),
        translationZh: z.string().trim().max(300).optional(),
        heat: z.number().finite().nonnegative().nullable().optional(),
        heatSupported: z.boolean().optional(),
        reason: z.string().trim().min(1).max(1e3)
      })
    ).max(5).default([]),
    rejectedKeywords: z.array(
      z.object({
        keyword: z.string().trim().min(1).max(300),
        translationZh: z.string().trim().max(300).optional(),
        reason: z.string().trim().min(1).max(1e3)
      })
    ).max(10).default([])
  }),
  gptPrompt: z.string().trim().min(1).max(5e4),
  webSearch: z.object({
    used: z.boolean(),
    resultBlockCount: z.number().int().nonnegative()
  })
});
var moneySchema = z.object({
  amount: z.number().finite().nonnegative(),
  currency: currencySchema
});
var nullableText = (maximum) => z.string().trim().min(1).max(maximum).nullable().default(null);
var analysisLogisticsInputSchema = z.object({
  volumeDivisor: z.number().positive().default(5e3),
  ratePerKg: moneySchema.nullable().default(null),
  firstLeg: moneySchema.nullable().default(null),
  tailLeg: moneySchema.nullable().default(null),
  handling: moneySchema.nullable().default(null),
  manualOverride: moneySchema.nullable().default(null)
});
var analysisTemporaryOverridesSchema = z.object({
  commissionRate: z.number().min(0).max(1).optional(),
  serviceFeeRate: z.number().min(0).max(1).optional(),
  advertisingRate: z.number().min(0).max(1).optional(),
  returnLossRate: z.number().min(0).max(1).optional(),
  taxRate: z.number().min(0).max(1).optional(),
  exchangeMarkupRate: z.number().min(0).max(1).optional(),
  targetMarginRate: z.number().min(0).max(0.95).optional(),
  fixedCosts: z.array(moneySchema).max(20).optional()
});
var analysisPlatformInputSchema = z.object({
  sellingPrice: moneySchema.nullable().default(null),
  costProfileId: z.string().uuid().nullable().default(null),
  temporaryOverrides: analysisTemporaryOverridesSchema.default({}),
  logistics: analysisLogisticsInputSchema.default({
    volumeDivisor: 5e3,
    ratePerKg: null,
    firstLeg: null,
    tailLeg: null,
    handling: null,
    manualOverride: null
  })
});
var createProductAnalysisSchema = z.object({
  mode: z.enum(["simple", "full"]).default("simple"),
  analysisMode: analysisModeSchema.default("standalone"),
  productName: z.string().trim().min(1).max(200),
  idea: nullableText(2e3),
  sourceKeyword: nullableText(200),
  productLink: z.string().url().nullable().default(null),
  sku: nullableText(200),
  material: nullableText(500),
  dimensions: nullableText(500),
  structure: nullableText(1e3),
  sourcePlatform: platformSchema.nullable().default(null),
  sourceKeywordId: z.string().uuid().nullable().default(null),
  platforms: z.array(platformSchema).min(1).max(1).transform((items) => [...new Set(items)]),
  resolvedProductId: z.string().uuid().nullable().default(null),
  forceNewProduct: z.boolean().default(false),
  netWeightKg: z.number().finite().nonnegative().nullable().default(null),
  packagedWeightKg: z.number().finite().nonnegative().nullable().default(null),
  lengthCm: z.number().finite().nonnegative().nullable().default(null),
  widthCm: z.number().finite().nonnegative().nullable().default(null),
  heightCm: z.number().finite().nonnegative().nullable().default(null),
  quantity: z.number().int().positive().max(1e5).default(1),
  purchasePrice: moneySchema.nullable().default(null),
  outputCurrency: currencySchema.default("CNY"),
  platformInputs: z.object({
    wb: analysisPlatformInputSchema.optional()
  }).default({})
});
var createCostProfileSchema = z.object({
  platform: platformSchema,
  fulfillmentMethod: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(120),
  defaultCurrency: currencySchema,
  config: z.object({
    commissionRate: z.number().min(0).max(1).default(0),
    serviceFeeRate: z.number().min(0).max(1).default(0),
    advertisingRate: z.number().min(0).max(1).default(0),
    returnLossRate: z.number().min(0).max(1).default(0),
    taxRate: z.number().min(0).max(1).default(0),
    exchangeMarkupRate: z.number().min(0).max(1).default(0),
    targetMarginRate: z.number().min(0).max(0.95).default(0.25),
    fixedCosts: z.array(moneySchema).max(20).default([]),
    logistics: analysisLogisticsInputSchema.default({
      volumeDivisor: 5e3,
      ratePerKg: null,
      firstLeg: null,
      tailLeg: null,
      handling: null,
      manualOverride: null
    })
  })
});
var createExchangeRateSchema = z.object({
  baseCurrency: currencySchema,
  quoteCurrency: currencySchema,
  rate: z.number().finite().positive(),
  source: z.string().trim().min(1).max(200),
  rateDate: z.string().datetime({ offset: true }),
  status: z.enum(["official", "settlement", "manual", "stale_fallback"]).default("manual")
}).refine((value) => value.baseCurrency !== value.quoteCurrency, {
  message: "Base and quote currencies must differ.",
  path: ["quoteCurrency"]
});
var restoreProductAnalysisSchema = z.object({
  expectedVersion: z.number().int().positive()
});
var requestExternalDataSchema = z.object({
  sources: z.array(z.enum(["public_web", "regulation", "platform_external"])).min(1).max(4).transform((items) => [...new Set(items)]),
  platforms: z.array(platformSchema).max(1).default([])
});
var createBackupSchema = z.object({
  protected: z.boolean().default(false),
  simulateFailure: z.boolean().default(false)
});
var restoreBackupSchema = z.object({
  confirmation: z.string().trim().min(1).max(120),
  reason: z.string().trim().min(8).max(1e3),
  expectedVersion: z.number().int().positive()
});
var createTestSnapshotSchema = z.object({
  platforms: z.array(platformSchema).min(1).max(1).transform((items) => [...new Set(items)]),
  sampleSize: z.number().int().min(1).max(1e5),
  confirmOverwrite: z.literal(true)
});
var releaseCandidateSchema = z.object({
  source: z.enum(["github_release", "simulation"]),
  tag: z.string().trim().min(1).max(100),
  version: z.string().trim().min(1).max(100),
  releaseNotes: z.string().trim().min(1).max(2e4),
  migrationSummary: z.string().trim().max(1e4),
  assetName: z.string().trim().min(1).max(255),
  assetSha256: z.string().regex(/^[a-fA-F0-9]{64}$/),
  assetUrl: z.string().url()
});
var simulateReleaseInstallSchema = z.object({
  releaseId: z.string().uuid(),
  confirmation: z.string().trim().min(1).max(120),
  failAt: z.enum([
    "confirm",
    "maintenance",
    "drain_jobs",
    "backup",
    "fetch_asset",
    "verify_asset",
    "migration_check",
    "switch_version",
    "migrate",
    "health_check",
    "complete"
  ]).optional()
});
var runCleanupSchema = z.object({
  confirmation: z.literal("RUN CLEANUP")
});
var jobStatusSchema = z.enum([
  "queued",
  "running",
  "paused",
  "cancelling",
  "cancelled",
  "succeeded",
  "failed"
]);
export {
  analysisLogisticsInputSchema,
  analysisModeSchema,
  analysisPlatformInputSchema,
  analysisTemporaryOverridesSchema,
  batchUpdateDecisionRequestSchema,
  competitorRefreshSchema,
  competitorSampleUpdateSchema,
  createBackupSchema,
  createCostProfileSchema,
  createDictionaryEntrySchema,
  createExchangeRateSchema,
  createProductAnalysisSchema,
  createPromptVersionSchema,
  createResearchRunSchema,
  createTestSnapshotSchema,
  createUploadRequestSchema,
  createUserRequestSchema,
  currencySchema,
  extractSeoProductRequestSchema,
  generateSeoPromptRequestSchema,
  interpretProductIdeaSchema,
  jobStatusSchema,
  keywordProcessingActionSchema,
  keywordRelationsSchema,
  keywordSearchRequestSchema,
  loginRequestSchema,
  manualWordstatSchema,
  moneySchema,
  platformSchema,
  platformsForAnalysisMode,
  preferenceValueSchema,
  productCardDraftSchema,
  productMaterialSchema,
  publicUserSchema,
  refreshResearchSuggestionsSchema,
  registerRequestSchema,
  releaseCandidateSchema,
  requestExternalDataSchema,
  researchCompetitorRefreshSchema,
  researchDecisionSchema,
  resetPasswordRequestSchema,
  restoreBackupSchema,
  restoreProductAnalysisSchema,
  runCleanupSchema,
  saveProductCardSchema,
  selectResearchKeywordsSchema,
  seoPlatformSchema,
  seoProductExtractionResultSchema,
  seoPromptGenerationResultSchema,
  seoPromptInformationRisksSchema,
  seoPromptKeywordContextSchema,
  seoPromptProductSchema,
  seoPromptTemplateVersionSchema,
  simulateReleaseInstallSchema,
  suggestSourceTypeSchema,
  updateDecisionRequestSchema,
  updateExtractedAttributeSchema,
  updateUserRequestSchema,
  userDecisionSchema,
  userRoleSchema,
  wordstatOpenedSchema
};
